import React, { FC, useMemo } from "react";

import { Col, OverlayTrigger, Row, Stack, Table, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import currency from "currency.js";

import {
  GetPaymentLineByResaleIdQuery,
  PaymentLineDetails,
  ResaleFragment,
  ResaleStatuses,
  useGetPaymentLineByResaleIdQuery,
} from "@api/graphql";
import { Loading } from "@components/loading";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { TableHeader } from "@components/table/TableContents";
import { useAuthenticationContext } from "@context/authentication";
import detailDrawerStyles from "@pages/dashboard/DetailsDrawer.module.scss";
import { Role } from "@roles";

import styles from "./RecipientsBreakdown.module.scss";

interface RecipientsBreakdownProps {
  resale: ResaleFragment;
}

const HEADERS: TableHeader<PaymentLineDetails>[] = [
  { label: "Recipient name", accessor: "payee_name" },
  { label: "% Share", accessor: "overall_percentage_share" },
  { label: "Royalty", accessor: "royalty_gross" },
  { label: "Admin fee", accessor: "admin_fee" },
  { label: "GST on admin", accessor: "gst_on_admin_fee" },
  { label: "Payment", accessor: "royalty_net" },
];

const CURRENCY_COLUMNS_ACCESSOR: Partial<keyof PaymentLineDetails>[] = [
  "royalty_net",
  "royalty_gross",
  "admin_fee",
  "gst_on_admin_fee",
];

const cellMapper = (accessor: keyof PaymentLineDetails, cellValue?: string | number) => {
  if (CURRENCY_COLUMNS_ACCESSOR.includes(accessor)) {
    return cellValue ? currency(cellValue).format() : currency(0).format();
  } else if (accessor === "overall_percentage_share") {
    return `${cellValue}%`;
  }

  return cellValue;
};

const INITIAL_SUM = {
  royalty_net: 0,
  royalty_gross: 0,
  admin_fee: 0,
  gst_on_admin_fee: 0,
  overall_percentage_share: 0,
};

const RecipientBreakdownTableHeader: FC<{ tableHeaders: TableHeader<PaymentLineDetails>[] }> = ({ tableHeaders }) => {
  return (
    <thead>
      <tr className={detailDrawerStyles["custom-header-color"]}>
        {tableHeaders.map(({ label, accessor }) => (
          <th key={String(accessor)} className={classNames(detailDrawerStyles["table-header-text"], "b4")}>
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );
};

const ALLOWED_RESALE_STATUS = [ResaleStatuses.CONFIRMED_ELIGIBLE, ResaleStatuses.RESEARCH_REQUIRED];

const RecipientBreakdownTableBody: FC<{
  tableHeaders: TableHeader<PaymentLineDetails>[];
  paymentLine: GetPaymentLineByResaleIdQuery["getPaymentLineByResaleId"] | undefined;
  resale: ResaleFragment;
  summedPaymentLines: Partial<PaymentLineDetails>;
}> = ({ tableHeaders, paymentLine, resale, summedPaymentLines }) => {
  const { resale_id: resaleId } = resale;

  return (
    <tbody>
      {paymentLine &&
        paymentLine.map((row) => {
          return (
            row && (
              <tr key={String(row.id)}>
                {tableHeaders.map(({ accessor }) =>
                  accessor === "payee_name" ? (
                    <OverlayTrigger
                      key={`${row.id}-${accessor}`}
                      placement="left"
                      overlay={<Tooltip>{cellMapper(accessor, row[accessor]) || "undefined"}</Tooltip>}
                    >
                      <td className="text-truncate">
                        <>{cellMapper(accessor, row[accessor])}</>
                      </td>
                    </OverlayTrigger>
                  ) : (
                    <td className="text-truncate" key={`${row.id}-${accessor}`}>
                      <>{cellMapper(accessor, row[accessor])}</>
                    </td>
                  ),
                )}
              </tr>
            )
          );
        })}
      {/* Total */}
      <tr key={`${String(resaleId)}-total`}>
        {tableHeaders.map(({ accessor }) => (
          <td key={`${resaleId}-total-${accessor}`}>
            {accessor === "payee_name" ? "Total" : cellMapper(accessor, summedPaymentLines[accessor] || 0)}
          </td>
        ))}
      </tr>
    </tbody>
  );
};

export const RecipientsBreakdown: React.FC<RecipientsBreakdownProps> = ({ resale }) => {
  const { resale_status: resaleStatus } = resale;
  const isResaleStatusAllowed = resaleStatus && ALLOWED_RESALE_STATUS.includes(resaleStatus);

  // check current user role
  const isRoyaltiesRecipient = useIsAuthUserAllowed([Role.RoyaltyRecipient]);

  // get current user participant_id
  const { auth } = useAuthenticationContext();
  const currentUserParticipantId = auth?.currentUserData?.participant?.participant_id;

  const { data, loading } = useGetPaymentLineByResaleIdQuery({
    variables: {
      resaleId: resale.resale_id,
    },
  });

  const paymentLines = data?.getPaymentLineByResaleId;

  //  royalty_recipient user MUST NOT see the % Share column
  const tableHeadersToDisplay = useMemo(() => {
    if (isRoyaltiesRecipient) {
      return HEADERS.filter(({ accessor }) => accessor !== "overall_percentage_share");
    }
    return HEADERS;
  }, [isRoyaltiesRecipient]);

  // royalty_recipient user must only see the row where they are the mentioned recipient
  const paymentLinesToDisplay = useMemo(() => {
    if (isRoyaltiesRecipient) {
      return paymentLines?.filter((row) => row?.payee_id === currentUserParticipantId);
    }
    return paymentLines;
  }, [currentUserParticipantId, isRoyaltiesRecipient, paymentLines]);

  const summedPaymentLines: Partial<PaymentLineDetails> = useMemo(() => {
    if (!paymentLinesToDisplay) {
      return INITIAL_SUM;
    }
    return paymentLinesToDisplay.reduce((acc, payment) => {
      return {
        royalty_net: acc.royalty_net + (payment?.royalty_net || 0),
        royalty_gross: acc.royalty_gross + (payment?.royalty_gross || 0),
        admin_fee: acc.admin_fee + (payment?.admin_fee || 0),
        gst_on_admin_fee: acc.gst_on_admin_fee + (payment?.gst_on_admin_fee || 0),
        overall_percentage_share: acc.overall_percentage_share + (payment?.overall_percentage_share || 0),
      };
    }, INITIAL_SUM);
  }, [paymentLinesToDisplay]);

  if (isResaleStatusAllowed && paymentLines) {
    return (
      <Stack gap={4} className="col-md-10">
        <Row>
          <span className={classNames(styles["stack-label"], "h5")}>Recipient breakdown</span>
        </Row>
        <Row>
          <Col>
            {loading ? (
              <Loading />
            ) : (
              <div className={classNames(styles["recipient-breakdown"])}>
                <Table borderless hover>
                  <RecipientBreakdownTableHeader tableHeaders={tableHeadersToDisplay} />
                  <RecipientBreakdownTableBody
                    tableHeaders={tableHeadersToDisplay}
                    paymentLine={paymentLinesToDisplay}
                    resale={resale}
                    summedPaymentLines={summedPaymentLines}
                  />
                </Table>
              </div>
            )}
          </Col>
        </Row>
      </Stack>
    );
  } else {
    return <></>;
  }
};
